<template>
  <v-app>
    <v-container>
      <!-- Editable area -->
      <v-card class="pa-4 mb-6">
        <v-card-title>Compose Message</v-card-title>
        <v-card-text>
          <div
              ref="editor"
              class="template-editor"
              contenteditable="true"
              @keydown="onKeydown"
              @input="onInput"
              @mouseup="saveSelection"
              @keyup="saveSelection"
              placeholder="Type here and select variables below…"
          ></div>
        </v-card-text>
      </v-card>

      <!-- Variable selects by group -->
      <v-card class="pa-4 mb-6">
        <v-card-title>Select Patient Variables</v-card-title>
        <v-card-text>
          <v-select
              v-model="selected.patient"
              :items="variables.patient"
              item-text="label"
              item-value="name"
              label="Patient"
              multiple
              chips
              outlined
              @change="onMultiSelect('patient')"
          />
        </v-card-text>
      </v-card>

      <v-card class="pa-4 mb-6">
        <v-card-title>Select Appointment Variables</v-card-title>
        <v-card-text>
          <v-select
              v-model="selected.appointment"
              :items="variables.appointment"
              item-text="label"
              item-value="name"
              label="Appointment"
              multiple
              chips
              outlined
              @change="onMultiSelect('appointment')"
          />
        </v-card-text>
      </v-card>

      <v-card class="pa-4 mb-6">
        <v-card-title>Select Doctor Variables</v-card-title>
        <v-card-text>
          <v-select
              v-model="selected.doctor"
              :items="variables.doctor"
              item-text="label"
              item-value="name"
              label="Doctor"
              multiple
              chips
              outlined
              @change="onMultiSelect('doctor')"
          />
        </v-card-text>
      </v-card>

      <!-- Raw model -->
      <v-card class="pa-4 mt-8">
        <v-card-title>Raw Model</v-card-title>
        <v-card-text>
          <pre>{{ model }}</pre>
        </v-card-text>
      </v-card>
    </v-container>
  </v-app>
</template>

<script>
export default {
  data() {
    return {
      // model is always a list of objects: { type: 'text'|'variable', content?, name?, label? }
      model: [],
      savedRange: null,
      selected: { patient: [], appointment: [], doctor: [] },
      variables: {
        patient: [
          { name: 'patient.firstName', label: 'First Name' },
          { name: 'patient.lastName',  label: 'Last Name' },
          { name: 'patient.fullName',  label: 'Full Name' },
          { name: 'patient.dob',       label: 'Date of Birth' },
        ],
        appointment: [
          { name: 'appointment.day_name',  label: 'Appointment Day Name' },
          { name: 'appointment.date',  label: 'Date' },
          { name: 'appointment.start', label: 'Start Time' },
          { name: 'appointment.end',   label: 'End Time' },
          { name: 'appointment.type',  label: 'Type' },
        ],
        doctor: [
          { name: 'doctor.title',      label: 'Title' },
          { name: 'doctor.fullName',   label: 'Full Name' },
          { name: 'doctor.firstName',  label: 'First Name' },
          { name: 'doctor.lastName',   label: 'Last Name' },
        ],
      },
    };
  },
  mounted() {
    this.renderEditor();
  },
  methods: {
    saveSelection() {
      const sel = window.getSelection();
      if (sel.rangeCount > 0 && this.$refs.editor.contains(sel.anchorNode)) {
        this.savedRange = sel.getRangeAt(0).cloneRange();
      }
    },
    onMultiSelect(group) {
      const arr = this.selected[group];
      arr.forEach(name => {
        const varItem = this.variables[group].find(v => v.name === name);
        if (varItem) this.insertVariable(varItem);
      });
      this.selected[group] = [];
    },
    renderEditor() {
      const el = this.$refs.editor;
      el.innerHTML = '';
      this.model.forEach(item => {
        if (item.type === 'text') {
          el.appendChild(document.createTextNode(item.content));
        } else if (item.type === 'variable') {
          const span = document.createElement('span');
          span.className = 'variable-token';
          span.dataset.name = item.name;
          span.contentEditable = 'false';
          span.innerText = item.label;
          el.appendChild(span);
        }
      });
      this.placeCaretAtEnd(el);
    },
    insertVariable(varItem) {
      const editor = this.$refs.editor;
      editor.focus();
      let range = this.savedRange;
      if (!range || !editor.contains(range.startContainer)) {
        range = document.createRange();
        range.selectNodeContents(editor);
        range.collapse(false);
      }
      const span = document.createElement('span');
      span.className = 'variable-token';
      span.dataset.name = varItem.name;
      span.contentEditable = 'false';
      span.innerText = varItem.label;
      range.deleteContents();
      range.insertNode(span);
      range.setStartAfter(span);
      range.collapse(true);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
      this.savedRange = range.cloneRange();
      this.syncModel();
    },
    onInput() {
      this.syncModel();
    },
    syncModel() {
      const el = this.$refs.editor;
      const newModel = [];
      el.childNodes.forEach(node => {
        if (node.nodeType === 3) {
          // text node
          if (node.textContent) {
            newModel.push({ type: 'text', content: node.textContent });
          }
        } else if (node.nodeType === 1 && node.classList.contains('variable-token')) {
          newModel.push({
            type: 'variable',
            name: node.dataset.name,
            label: node.innerText,
          });
        }
      });
      this.model = newModel;
    },
    onKeydown(e) {
      if (e.key === 'Backspace' || e.key === 'Delete') {
        const sel = window.getSelection();
        if (!sel.rangeCount) return;
        let node = sel.anchorNode;
        if (node.nodeType === 3 && node.parentNode.classList.contains('variable-token')) {
          node = node.parentNode;
        }
        if (node.nodeType === 1 && node.classList.contains('variable-token')) {
          e.preventDefault();
          this.model = this.model.filter(
              item => !(item.type === 'variable' && item.name === node.dataset.name)
          );
          this.$nextTick(this.renderEditor);
        }
      }
    },
    placeCaretAtEnd(el) {
      el.focus();
      document.execCommand('selectAll', false, null);
      document.getSelection().collapseToEnd();
    },
  },
};
</script>

<style>
.template-editor {
  min-height: 100px;
  border: 1px solid #ccc;
  padding: 8px;
  border-radius: 4px;
  outline: none;
  white-space: pre-wrap;
}
.template-editor:empty:before {
  content: attr(placeholder);
  color: #999;
}
.variable-token {
  background-color: #e0f7fa;
  border-radius: 4px;
  padding: 2px 6px;
  margin: 0 1px;
  font-weight: 500;
}
</style>
